/**
 * The runtime half of page animations (see page-motion.ts): presets, labels, keyframes and
 * timing, with no schema library, so a creator site's script stays small.
 */
export const motionTriggers = ["load", "in-view", "hover", "press"] as const;
export type MotionTrigger = (typeof motionTriggers)[number];

export const motionEffectOptions = {
  load: ["rise", "fade", "scale", "blur"],
  "in-view": ["rise", "fade", "scale", "blur"],
  hover: ["lift", "grow"],
  press: ["shrink"],
} as const satisfies Record<MotionTrigger, readonly string[]>;
export const motionEffects = ["rise", "fade", "scale", "blur", "lift", "grow", "shrink"] as const;
export type MotionEffect = (typeof motionEffects)[number];

export const motionEasings = ["spring", "ease-out", "ease-in-out", "linear"] as const;
export type MotionEasing = (typeof motionEasings)[number];

export type PageMotion = {
  trigger: MotionTrigger;
  effect: MotionEffect;
  delay: number;
  duration: number;
  easing: MotionEasing;
  bounce: number;
};

export const motionLimits = {
  delay: { min: 0, max: 3 },
  duration: { min: 0.1, max: 2 },
  bounce: { min: 0, max: 0.6 },
} as const;

export const motionTriggerLabels: Record<MotionTrigger, string> = {
  load: "On load",
  "in-view": "In view",
  hover: "Hover",
  press: "Press",
};
export const motionEffectLabels: Record<MotionEffect, string> = {
  rise: "Rise",
  fade: "Fade",
  scale: "Scale",
  blur: "Blur",
  lift: "Lift",
  grow: "Grow",
  shrink: "Shrink",
};
export const motionEasingLabels: Record<MotionEasing, string> = {
  spring: "Spring",
  "ease-out": "Ease out",
  "ease-in-out": "Ease in out",
  linear: "Linear",
};

export const isEntrance = (trigger: MotionTrigger) => trigger === "load" || trigger === "in-view";

/** A new animation of a kind, with the house's defaults. */
export function defaultMotion(trigger: MotionTrigger): PageMotion {
  return isEntrance(trigger)
    ? { trigger, effect: "rise", delay: 0, duration: 0.6, easing: "spring", bounce: 0.1 }
    : {
        trigger,
        effect: trigger === "hover" ? "lift" : "shrink",
        delay: 0,
        duration: 0.3,
        easing: "spring",
        bounce: 0.25,
      };
}

/** Changing the kind keeps the timing and picks an effect that fits it. */
export function withTrigger(motion: PageMotion, trigger: MotionTrigger): PageMotion {
  const options = motionEffectOptions[trigger] as readonly MotionEffect[];
  return {
    ...(isEntrance(trigger) === isEntrance(motion.trigger) ? motion : defaultMotion(trigger)),
    trigger,
    effect: options.includes(motion.effect) ? motion.effect : options[0]!,
  };
}

export type MotionKeyframes = { opacity?: number[]; transform?: string[]; filter?: string[] };

/**
 * The keyframes of an effect, from rest to its end: entrances run from their first frame,
 * hover and press run forward on the way in and back on the way out. Reduced motion keeps the
 * fade of an entrance and drops movement, blur and interaction effects.
 */
export function motionKeyframes(effect: MotionEffect, reduced = false): MotionKeyframes {
  if (reduced) return ["rise", "fade", "scale", "blur"].includes(effect) ? { opacity: [0, 1] } : {};
  switch (effect) {
    case "rise":
      return { opacity: [0, 1], transform: ["translateY(24px)", "translateY(0px)"] };
    case "fade":
      return { opacity: [0, 1] };
    case "scale":
      return { opacity: [0, 1], transform: ["scale(0.94)", "scale(1)"] };
    case "blur":
      return { opacity: [0, 1], filter: ["blur(10px)", "blur(0px)"] };
    case "lift":
      return { transform: ["translateY(0px)", "translateY(-4px)"] };
    case "grow":
      return { transform: ["scale(1)", "scale(1.03)"] };
    case "shrink":
      return { transform: ["scale(1)", "scale(0.96)"] };
  }
}

export const motionCurves: Record<
  Exclude<MotionEasing, "spring">,
  [number, number, number, number]
> = {
  "ease-out": [0.22, 1, 0.36, 1],
  "ease-in-out": [0.65, 0, 0.35, 1],
  linear: [0, 0, 1, 1],
};

/** Motion's spring generator, passed in so this module never loads Motion itself. */
export type SpringEasing = (visualDuration: number, bounce: number) => unknown;

/**
 * An animation's timing for the Web Animations API: delay and duration in milliseconds and a
 * CSS easing. A spring runs until it settles, so its duration is Motion's, not the visual one.
 */
export function motionTiming(
  motion: Pick<PageMotion, "delay" | "duration" | "easing" | "bounce">,
  spring: SpringEasing,
) {
  if (motion.easing === "spring") {
    const css = String(spring(motion.duration, motion.bounce));
    const match = /^([\d.]+)ms\s+(.*)$/.exec(css);
    return {
      delay: motion.delay * 1000,
      duration: match ? Number(match[1]) : motion.duration * 1000,
      easing: match ? match[2]! : "ease-out",
    };
  }
  return {
    delay: motion.delay * 1000,
    duration: motion.duration * 1000,
    easing: `cubic-bezier(${motionCurves[motion.easing].join(", ")})`,
  };
}

/** Motion's own options for an animation, for animate() on a site. */
export function motionOptions(
  motion: Pick<PageMotion, "delay" | "duration" | "easing" | "bounce">,
  spring: unknown,
  extraDelay = 0,
) {
  const delay = motion.delay + extraDelay;
  return motion.easing === "spring"
    ? { type: spring, visualDuration: motion.duration, bounce: motion.bounce, delay }
    : { duration: motion.duration, ease: motionCurves[motion.easing], delay };
}

/** Repeated items in a collection follow one another by this much. */
export const motionStagger = 0.06;
